import LegalLayout, { Section } from "@/components/LegalLayout";
import { useSeo } from "@/lib/useSeo";

export default function Privacy() {
  useSeo({
    title: "Privacy Policy | Social Media Combo",
    description:
      "How Social Media Combo collects, uses, and protects your information when you visit socialmediacombo.com or purchase our digital products.",
    path: "/privacy",
  });
  return (
    <LegalLayout title="Privacy Policy" updated="June 2026">
      <p className="text-[#666666] leading-relaxed">
        This Privacy Policy explains how Social Media Combo ("we", "us", or "our") collects,
        uses, and protects your information when you visit <strong>socialmediacombo.com</strong>{" "}
        (the "Site") or purchase our digital products. By using the Site, you agree to the
        practices described below.
      </p>

      <Section heading="1. Information We Collect">
        <p>We collect the following types of information:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>Information you provide:</strong> your email address when you subscribe to our newsletter or request our free ebook, and your name and email when you make a purchase.</li>
          <li><strong>Payment information:</strong> processed securely by our payment provider, Stripe. We do not store or have access to your full card details.</li>
          <li><strong>Automatically collected data:</strong> IP address, browser type, device information, pages visited, and referring URLs, gathered via cookies and analytics tools.</li>
        </ul>
      </Section>

      <Section heading="2. How We Use Your Information">
        <ul className="list-disc pl-6 space-y-2">
          <li>To deliver the digital products and free resources you request.</li>
          <li>To process payments and send order confirmations.</li>
          <li>To send you newsletters, product updates, offers, and marketing emails (you may unsubscribe at any time).</li>
          <li>To operate, maintain, analyze, and improve the Site.</li>
          <li>To detect and prevent fraud and abuse.</li>
        </ul>
      </Section>

      <Section heading="3. Third-Party Services">
        <p>We rely on trusted third parties to run our business. Each has its own privacy policy:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>Stripe</strong> — payment processing.</li>
          <li><strong>Brevo</strong> — email marketing and newsletter delivery.</li>
          <li><strong>Google Analytics</strong> — website usage analytics.</li>
        </ul>
        <p>We do not sell or rent your personal information to any third party.</p>
      </Section>

      <Section heading="4. Cookies & Analytics">
        <p>
          We use cookies and similar technologies (including Google Analytics) to understand how
          visitors use the Site and to improve your experience. You can disable cookies through
          your browser settings, though some features may not function correctly as a result.
        </p>
      </Section>

      <Section heading="5. Email Marketing">
        <p>
          If you subscribe to our list, we will send you emails about our products, offers, and
          content. Every email includes an unsubscribe link, and you can opt out at any time.
          We use double opt-in confirmation where required.
        </p>
      </Section>

      <Section heading="6. Your Rights">
        <p>
          Depending on your location, you may have the right to access, correct, delete, or
          restrict the processing of your personal data, and to object to marketing. To exercise
          these rights, contact us using the details below. We will respond within a reasonable
          timeframe and in accordance with applicable law (including GDPR and CCPA where relevant).
        </p>
      </Section>

      <Section heading="7. Data Retention & Security">
        <p>
          We retain your information only as long as necessary to fulfill the purposes described
          in this policy or as required by law. We use reasonable technical and organizational
          measures to protect your data, but no method of transmission over the internet is 100%
          secure.
        </p>
      </Section>

      <Section heading="8. Children's Privacy">
        <p>
          The Site is not intended for individuals under the age of 18, and we do not knowingly
          collect personal information from children.
        </p>
      </Section>

      <Section heading="9. Changes to This Policy">
        <p>
          We may update this Privacy Policy from time to time. Changes will be posted on this page
          with an updated "Last updated" date.
        </p>
      </Section>

      <Section heading="10. Contact Us">
        <p>
          If you have any questions about this Privacy Policy, contact us at{" "}
          <a href="mailto:ebooks@socialmediacombo.com" className="text-[#F59E0B] underline">
            ebooks@socialmediacombo.com
          </a>.
        </p>
      </Section>
    </LegalLayout>
  );
}
